import moment from "moment";
import * as React from "react";
import { ExportType } from "@app/exports/enumerations";
import { ComparisonProviderType, NetworkAssessmentAnalysisType, TimeFrequency } from "@app/projects/enumerators";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { ServicingProvider, ServicingProviderComparison } from "@app/projects/results/servicing-provider";
import { RegionGranularity } from "@app/region-sets";
import { Link } from "react-router-dom";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ACCESS_POINT_PROFILE_FLYOUT_TITLE } from "@app/projects/servicing-provider/servicingProviderProfileConstants";
import { NetworkProvidersComparisonTableData } from "@app/projects/servicing-provider/types";
import { FlyoutLink } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { JSX } from "react";
import { withZipContextState } from "@app/projects/zip-projects/ZipProjectContext";
import { ZipContextState } from "@app/projects/zip-projects/interfaces/zipContextState";
import { ResourceType } from "@app/resources/resource";
import { getPrimaryAndSecondaryRunConfigurations } from "@app/projects/network-assessment/adequacy/utility";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { shouldSwitchProjectRunConfigurationsInCompareMode } from "@app/projects/helpers";
import { getSearchParametersFromFilterSelections } from "@app/projects/zip-projects/utility";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import ProjectService from "@app/projects/projectService";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import formatPhoneNumber from "@app/shared/formatPhoneNumber";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

type NetworkProvidersComparisonPanelProps = FeatureFlagContextProps & ZipContextState & {
  className?: string;
  exportComparedRunConfigurationId?: number;
  exportEndpoint: string;
  projectURL: string;
  regionSetId: number;
  specialtyGroupCode: string;
};

interface NetworkProvidersComparisonPanelState {
  columnConfig: ColumnConfig;
  isInitialLoad: boolean;
  isOpen: boolean;
  lastSortedColumn: keyof NetworkProvidersComparisonTableData;
  lastSortedDirection: SortDirections;
  loadStatus: LoadStatus;
  offset: number;
  showAddedProviders: boolean;
  showMatchedProviders: boolean;
  showRemovedProviders: boolean;
  sortedRecords: ServicingProviderComparison[];
  tableData: NetworkProvidersComparisonTableData[];
  totalCount: number;
}

type LoadStatus = "empty" | "error" | "loaded" | "loading" | "partialLoading" | "shouldLoad";

class NetworkProvidersComparisonPanel extends React.Component<NetworkProvidersComparisonPanelProps & CountyProjectContextProps, NetworkProvidersComparisonPanelState> {
  private readonly batchCount: number = 50;
  private readonly projectService = new ProjectService();
  private isZipProject: boolean;
  private latestRequestSequence: number = 0;

  constructor(props: NetworkProvidersComparisonPanelProps & CountyProjectContextProps) {
    super(props);

    this.state = {
      columnConfig: this.getColumnConfig(),
      isInitialLoad: true,
      isOpen: false,
      lastSortedColumn: undefined,
      lastSortedDirection: undefined,
      loadStatus: "shouldLoad",
      offset: 0,
      showAddedProviders: true,
      showMatchedProviders: true,
      showRemovedProviders: true,
      sortedRecords: [],
      tableData: [],
      totalCount: 0,
    };
    this.isZipProject = this.props.project.resource.resourceType === ResourceType.ZipAnalysisProject;
  }

  public async componentDidUpdate() {
    if (!this.props.compareToRunConfiguration) {
      return;
    }
    if (this.props.shouldRefreshNetworkProviders) {
      this.props.setShouldRefreshNetworkProviders(false);
      this.setState({ offset: 0 }, async () => { await this.buildServicingProviders(true, true); });
    }
  }

  public render() {
    if (!this.props.compareToRunConfiguration) {
      return;
    }
    const isLoading = !this.state || (this.state.loadStatus === "loading" || this.state.loadStatus === "shouldLoad");
    const shouldShowBatchCount = !!this.state && (this.state.isOpen && (this.state.loadStatus === "loaded" || this.state.loadStatus === "partialLoading"));
    const title = `Network Servicing Providers${shouldShowBatchCount ? ` (${this.state.sortedRecords.length} of ${this.state.totalCount})` : ""}`;

    return (
      <AccordionContainer className={this.props.className}>
        <Accordion
          defaultOpen={false}
          title={title}
          onPanelWillOpen={isLoading ? async () => await this.buildServicingProviders(true) : null}
          onPanelDidOpen={this.handleOnOpen}
          onPanelDidClose={this.handleOnClose}
          testId="network-providers-comparison-panel__network-servicing-providers-accordion"
        >
          {this.renderProviderTable()}
        </Accordion>
      </AccordionContainer>
    );
  }

  /**
   * Sets the isOpen flag on state to true.
   */
  private handleOnOpen = () => {
    this.setState({ isOpen: true });
  }

  /**
   * Sets the isOpen flag on state to false.
   */
  private handleOnClose = () => {
    this.setState({ isOpen: false });
  }

  /**
   * Handles Added, Removed, and Matched toggle clicks. If the toggling would cause no filters to be applied, all of them are automatically applied.
   * @param comparisonType The type of comparison that is being toggled (to filter) on or off
   */
  private handleProviderComparisonTypeToggleClick = async (comparisonType: ComparisonProviderType) => {
    switch (comparisonType) {
      case ComparisonProviderType.Removed:
        if (!this.state.showAddedProviders && !this.state.showMatchedProviders && this.state.showRemovedProviders) {
          // This action would leave it so no toggles are selected, so we set them all to be selected
          this.setState({
            offset: 0,
            showAddedProviders: true,
            showMatchedProviders: true,
            showRemovedProviders: true,
          }, async () => { await this.buildServicingProviders(false, true); });
        } else {
          this.setState((prevState) => ({
            offset: 0,
            showRemovedProviders: !prevState.showRemovedProviders,
          }), async () => { await this.buildServicingProviders(false, true); });
        }
        break;
      case ComparisonProviderType.Added:
        if (this.state.showAddedProviders && !this.state.showMatchedProviders && !this.state.showRemovedProviders) {
          // This action would leave it so no toggles are selected, so we set them all to be selected
          this.setState({
            offset: 0,
            showAddedProviders: true,
            showMatchedProviders: true,
            showRemovedProviders: true,
          }, async () => { await this.buildServicingProviders(false, true); });
        } else {
          this.setState((prevState) => ({
            offset: 0,
            showAddedProviders: !prevState.showAddedProviders,
          }), async () => { await this.buildServicingProviders(false, true); });
        }
        break;
      case ComparisonProviderType.Matched:
        if (!this.state.showAddedProviders && this.state.showMatchedProviders && !this.state.showRemovedProviders) {
          // This action would leave it so no toggles are selected, so we set them all to be selected
          this.setState({
            offset: 0,
            showAddedProviders: true,
            showMatchedProviders: true,
            showRemovedProviders: true,
          }, async () => { await this.buildServicingProviders(false, true); });
        } else {
          this.setState((prevState) => ({
            offset: 0,
            showMatchedProviders: !prevState.showMatchedProviders,
          }), async () => { await this.buildServicingProviders(false, true); });
        }
        break;
    }
  }

  /**
   * Gets the header content of the table, with details about the comparison.
   */
  private getTableComparisonHeader = () => {
    const { compareToRunConfiguration, runConfiguration } = this.props;
    let { compareToRunConfigurationTimestamp, runConfigurationTimestamp } = this.props;

    if (!compareToRunConfigurationTimestamp) {
      compareToRunConfigurationTimestamp = moment.utc(compareToRunConfiguration && (compareToRunConfiguration.dateCreated));
    }
    if (!runConfigurationTimestamp) {
      runConfigurationTimestamp = moment.utc(runConfiguration && (runConfiguration.dateCreated));
    }

    // Find the first to display.
    let firstTimestampDisplay = runConfiguration.isProjected ? "Projected" : runConfiguration.isTermed ? "Termed" : runConfiguration.isProjectedTermed ? "Projected-Termed" : runConfigurationTimestamp.isValid() ? runConfigurationTimestamp.format("MMMM YYYY") : "&nbsp;";

    // Then the second.
    let secondTimestampDisplay = compareToRunConfiguration.isProjected ? "Projected" : compareToRunConfiguration.isTermed ? "Termed" : compareToRunConfiguration.isProjectedTermed ? "Projected-Termed" : compareToRunConfigurationTimestamp.isValid() ? compareToRunConfigurationTimestamp.format("MMMM YYYY") : "&nbsp;";

    const firstTimestamp = new Date(firstTimestampDisplay);
    if (firstTimestamp > new Date(secondTimestampDisplay) || (firstTimestamp instanceof Date && isNaN(firstTimestamp.valueOf()))) {
      const tempTimestamp = secondTimestampDisplay;
      secondTimestampDisplay = firstTimestampDisplay;
      firstTimestampDisplay = tempTimestamp;
    }

    return (
      <React.Fragment>
        {this.props.isDualMembershipMode ?
          <p>The following is a comparison of network servicing providers between {this.props.runConfiguration.memberSetAlias} and {this.props.project.projectRunConfigurations.find((config) => config.projectRunConfigurationId === this.props.runConfiguration.companionProjectRunConfigurationId).memberSetAlias} memberships{this.props.runConfigurationTimestamp ? ` in ${this.props.runConfigurationTimestamp.format("MMMM YYYY")}.` : "."}</p> :
          <p>The following is a comparison of network servicing providers between {firstTimestampDisplay} and {secondTimestampDisplay}...</p>
        }
        <div className="pull-right">
          <QESButton
            qesType="secondary"
            qesTestId="network-providers-comparison-panel__toggle-added-providers"
            className="fw-normal p-0 me-3"
            onClick={() => { this.handleProviderComparisonTypeToggleClick(ComparisonProviderType.Added); }}
            disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
          >
            <QesIcon
              iconName={this.state.showAddedProviders ? FontAwesome.ToggleOn : FontAwesome.ToggleOff}
              description={`Toggle the display of added providers ${this.state.showAddedProviders ? "off" : "on"}`}
              width="16"
              className="align-baseline"
            /> Added
          </QESButton>
          <QESButton
            qesType="secondary"
            qesTestId="network-providers-comparison-panel__toggle-removed-providers"
            className="fw-normal p-0 me-3"
            onClick={() => { this.handleProviderComparisonTypeToggleClick(ComparisonProviderType.Removed); }}
            disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
          >
            <QesIcon
              iconName={this.state.showRemovedProviders ? FontAwesome.ToggleOn : FontAwesome.ToggleOff}
              description={`Toggle the display of removed providers ${this.state.showRemovedProviders ? "off" : "on"}`}
              width="16"
              className="align-baseline"
            /> Removed
          </QESButton>
          <QESButton
            qesType="secondary"
            qesTestId="network-providers-comparison-panel__toggle-matched-providers"
            className="fw-normal p-0"
            onClick={() => { this.handleProviderComparisonTypeToggleClick(ComparisonProviderType.Matched); }}
            disabled={!(this.state.loadStatus === "loaded" || this.state.loadStatus === "empty")}
          >
            <QesIcon
              iconName={this.state.showMatchedProviders ? FontAwesome.ToggleOn : FontAwesome.ToggleOff}
              description={`Toggle the display of matched providers ${this.state.showMatchedProviders ? "off" : "on"}`}
              width="16"
              className="align-baseline"
            /> Matched
          </QESButton>
        </div>
        <div className="clearfix" />
      </React.Fragment>
    );
  }

  /**
   * Gets the render content of everything inside the provider comparison panel.
   */
  private renderProviderTable = () => {
    return (
      <React.Fragment>
        {this.getTableComparisonHeader()}
        <Waffles<NetworkProvidersComparisonTableData>
          bulkActionBar={(
            <span className="bulk-action-group">
              <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
                to={this.getExportLink()}
                data-testid="network-providers-comparison-panel__create-report-button"
              >
                <QesIcon iconName={FontAwesome.CloudDownload} isDecorative className="align-baseline" /> Create Report
              </Link>
            </span>
          )}
          columns={this.state.columnConfig}
          customSort={this.handleSort}
          data={this.state.tableData}
          fullWidth={true}
          loading={this.state.loadStatus !== "loaded" && this.state.loadStatus !== "empty"}
          loadMore={this.state.totalCount > 0 && this.handleLoadMore}
          passThroughSorting={true}
          testId="compared-network-providers"
        />

      </React.Fragment>
    );
  }

  private getColumnConfig = () => {
    const config: ColumnConfig = {
      address: {
        displayText: "Address",
        testId: "address",
      },
      comparisonProviderType: {
        displayText: "Change",
        testId: "comparison-provider-change",
      },
      displayName: {
        displayText: "Name",
        testId: "name",
      },
      medicareProviderOptOut: {
        displayText: "Medicare",
        testId: "medicare-opt-out",
      },
      providerDaysSinceLastAttested: {
        displayText: "Attestation Age",
        hidden: !this.props.project.showAttestation,
        testId: "days-since-last-attested",
      },
      servesPercent: {
        displayText: "Serves (%)",
        testId: "serves-percent",
      },
    };
    return config;
  }

  private buildTable = (inputData: ServicingProviderComparison[]) => {
    const topData: NetworkProvidersComparisonTableData[] = [];
    /* oxlint-disable sort-keys */
    for (const comparison of inputData) {
      topData.push({
        [wafflesKeyColumnName]: comparison.selectedProviderProviderId ? comparison.selectedProviderProviderId.toString() : comparison.comparedProviderProviderId.toString(),
        comparisonProviderType: (
          <>
            <div>{comparison.comparisonProviderType}</div>
            {comparison.selectedProviderProcessed === false || comparison.comparedProviderProcessed === false ? <div className="demibold">UNPROCESSED</div> : ""}
          </>
        ),
        displayName: (() => {
          const topProviderName = comparison.selectedProviderName;
          const bottomProviderName = comparison.comparedProviderName;
          const topProviderNPI = comparison.selectedProviderNPI;
          const bottomProviderNPI = comparison.comparedProviderNPI;
          const topProviderTIN = comparison.selectedProviderTIN;
          const bottomProviderTIN = comparison.comparedProviderTIN;

          const topProviderNameElement = topProviderName ? (
            <div className="compared-providers-content">
              <span className="d-block">
                <FlyoutLink
                  classNames="p-0 qes-button-secondary"
                  content={{
                    body: this.getProviderProfile(comparison, false, shouldSwitchProjectRunConfigurationsInCompareMode(this.props.runConfiguration, this.props.compareToRunConfiguration) ? this.props.compareToRunConfiguration : this.props.runConfiguration) as JSX.Element,
                    title: ACCESS_POINT_PROFILE_FLYOUT_TITLE,
                  }}
                  testId={`network-providers-comparison-panel__provider-profile-flyout--${comparison.selectedProviderProviderId}`}
                >
                  <QesIcon iconName={FontAwesome.UserMd} isDecorative width="11" height="14" className="align-text-top" /> {topProviderName}
                </FlyoutLink>
              </span>
              <span className="text-muted pl10 ml7 d-block">NPI: {topProviderNPI}</span>
              <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
                {!!topProviderTIN && (
                  <span className="text-muted ml15 d-block">TIN: {topProviderTIN}</span>
                )}
              </RoleRestricted>
            </div>) : null;
          const bottomProviderNameElement = bottomProviderName ? (
            <div className="compared-providers-content">
              <span className="d-block">
                <FlyoutLink
                  classNames="p-0 qes-button-secondary"
                  content={{
                    body: this.getProviderProfile(comparison, true, shouldSwitchProjectRunConfigurationsInCompareMode(this.props.runConfiguration, this.props.compareToRunConfiguration) ? this.props.runConfiguration : this.props.compareToRunConfiguration) as JSX.Element,
                    title: ACCESS_POINT_PROFILE_FLYOUT_TITLE,
                  }}
                  testId={`network-providers-comparison-panel__provider-profile-flyout--${comparison.comparedProviderProviderId}`}
                >
                  <QesIcon iconName={FontAwesome.UserMd} isDecorative width="11" height="14" className="align-text-top" /> {bottomProviderName}
                </FlyoutLink>
              </span>
              <span className="text-muted pl10 ml7 d-block">NPI: {bottomProviderNPI}</span>
              <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
                {!!bottomProviderTIN && (
                  <span className="text-muted ml15 d-block">TIN: {bottomProviderTIN}</span>
                )}
              </RoleRestricted>
            </div>) : null;

          return (
            <React.Fragment>
              {topProviderNameElement}
              {bottomProviderNameElement}
            </React.Fragment>
          );
        })(),
        address: (() => {
          const topProviderAddress = this.getAddressDisplay(comparison.selectedProviderAddress, comparison.selectedProviderAddress2, comparison.selectedProviderCity, comparison.selectedProviderState, comparison.selectedProviderZip, comparison.selectedProviderPhone, comparison.selectedProviderFax);
          const bottomProviderAddress = this.getAddressDisplay(comparison.comparedProviderAddress, comparison.comparedProviderAddress2, comparison.comparedProviderCity, comparison.comparedProviderState, comparison.comparedProviderZip, comparison.comparedProviderPhone, comparison.comparedProviderFax);

          return (
            <>
              <div className="compared-providers-content">{topProviderAddress}</div>
              <div className="compared-providers-content">{bottomProviderAddress}</div>
            </>
          );
        })(),
        servesPercent: (() => {
          const topServesPercent = comparison.selectedProviderServesPercent;
          const bottomServesPercent = comparison.comparedProviderServesPercent;
          
          const topServesPercentElement = !isNaN(Number(topServesPercent)) ? <React.Fragment>{`${(+topServesPercent).toFixed(1)} %`}</React.Fragment> : null;
          const bottomServesPercentElement = !isNaN(Number(bottomServesPercent)) ? <React.Fragment>{`${(+bottomServesPercent).toFixed(1)} %`}</React.Fragment> : null;

          return (
            <>
              <div className="compared-providers-content">{topServesPercentElement}</div>
              <div className="compared-providers-content">{bottomServesPercentElement}</div>
            </>
          );
        })(),
        medicareProviderOptOut: (() => {
          let topProviderAcceptsMedicareAssignmentElement: JSX.Element = null;
          let bottomProviderAcceptsMedicareAssignmentElement: JSX.Element = null;

          const topProviderMedicareOptOut = comparison.selectedProviderMedicareOptOut;
          const bottomProviderMedicareOptOut = comparison.comparedProviderMedicareOptOut;
          const topProviderAcceptsMedicareAssignment = comparison.selectedProviderAcceptsMedicareAssignment;
          const bottomProviderAcceptsMedicareAssignment = comparison.comparedProviderAcceptsMedicareAssignment;

          if (topProviderMedicareOptOut) {
            // If the provider's medicare opt out flag is set, he or she is not accepting Medicare, so display the ban symbol
            topProviderAcceptsMedicareAssignmentElement = <React.Fragment><QesIcon iconName={FontAwesome.Ban} description="This provider does not accept medicare" width="12" /></React.Fragment>;
          } else {
            switch (topProviderAcceptsMedicareAssignment) {
              case undefined:
                // If this is undefined, we do not know if the provider accepts medicare or not, so display nothing
                topProviderAcceptsMedicareAssignmentElement = null;
                break;
              case true:
                // If true, the provider accepts medicare, so display a check mark
                topProviderAcceptsMedicareAssignmentElement = <React.Fragment><QesIcon iconName={FontAwesome.Check} description="This provider accepts medicare" width="14" /></React.Fragment>;
                break;
              case false:
                // If false, we know the provider does not accept medicare, so display a ban symbol
                topProviderAcceptsMedicareAssignmentElement = <React.Fragment><QesIcon iconName={FontAwesome.Ban} description="This provider does not accept medicare" width="12" /></React.Fragment>;
                break;
            }
          }

          if (bottomProviderMedicareOptOut) {
            bottomProviderAcceptsMedicareAssignmentElement = <React.Fragment><QesIcon iconName={FontAwesome.Ban} description="This provider does not accept medicare" width="12" /></React.Fragment>;
          } else {
            switch (bottomProviderAcceptsMedicareAssignment) {
              case undefined:
                bottomProviderAcceptsMedicareAssignmentElement = null;
                break;
              case true:
                bottomProviderAcceptsMedicareAssignmentElement = <React.Fragment><QesIcon iconName={FontAwesome.Check} description="This provider accepts medicare" width="14" /></React.Fragment>;
                break;
              case false:
                bottomProviderAcceptsMedicareAssignmentElement = <React.Fragment><QesIcon iconName={FontAwesome.Ban} description="This provider does not accept medicare" width="12" /></React.Fragment>;
                break;
            }
          }

          return (
            <React.Fragment>
              <div className="compared-providers-content">{topProviderAcceptsMedicareAssignmentElement}</div>
              <div className="compared-providers-content">{bottomProviderAcceptsMedicareAssignmentElement}</div>
            </React.Fragment>
          );
        })(),
        providerDaysSinceLastAttested: (() => {
          let topProviderAttestationInfoElement = <React.Fragment><QesIcon iconName={FontAwesome.QuestionCircle} isDecorative width="12" className="align-baseline" /> Not Attested</React.Fragment>;
          let bottomProviderAttestationInfoElement = <React.Fragment><QesIcon iconName={FontAwesome.QuestionCircle} isDecorative width="12" className="align-baseline" /> Not Attested</React.Fragment>;

          const topProviderDaysSinceLastAttested = comparison.selectedProviderDaysSinceLastAttested;
          const bottomProviderDaysSinceLastAttested = comparison.comparedProviderDaysSinceLastAttested;

          if ((comparison.comparisonProviderType === ComparisonProviderType.Matched || (comparison.comparisonProviderType === ComparisonProviderType.Removed)) && topProviderDaysSinceLastAttested) {
            // If we have a match, AND the top provider has days since last attested
            // -OR- if the compared provider is on top, AND he was added, AND the top provider has days since last attested
            // -OR- if the compared is on the bottom, AND he was removed, AND the top provider has days since last attested
            topProviderAttestationInfoElement = (
              <React.Fragment>
                {topProviderDaysSinceLastAttested > 90
                  // color is "warning" yellow equivalent to $qes-tertiary-yellow-2
                  ? <QesIcon iconName={FontAwesome.CheckCircle} color={"#ffd654"} description="Warning: this provider was attested more than 90 days ago" className="align-text-top" width="12" />
                  // color is "success" green equivalent to $qes-tertiary-green-2
                  : <QesIcon iconName={FontAwesome.CheckCircle} color={"#00cd87"} isDecorative className="align-text-top" width="12" />
                }
                {topProviderDaysSinceLastAttested} days prior<br /><br /><br /><br /><br />
              </React.Fragment>);
          } else if (comparison.comparisonProviderType !== ComparisonProviderType.Matched && ((comparison.comparisonProviderType !== ComparisonProviderType.Removed))) {
            // If the compared provider is on bottom, AND he was not removed, AND we do not have a match
            // -OR- if the compared provider is on top, AND he was not added, AND we do not have a match
            topProviderAttestationInfoElement = null;
          }

          if ((comparison.comparisonProviderType === ComparisonProviderType.Matched || (comparison.comparisonProviderType === ComparisonProviderType.Added)) && bottomProviderDaysSinceLastAttested) {
            // If we have a match, AND the bottom provider has days since last attested
            // -OR- if the compared provider is on bottom, AND he was added, AND the bottom provider has days since last attested
            // -OR- if the compared provider is on top, AND he was removed, AND the bottom provider has days since last attested
            bottomProviderAttestationInfoElement = (
              <React.Fragment>
                {bottomProviderDaysSinceLastAttested > 90
                  // color is "warning" yellow equivalent to $qes-tertiary-yellow-2
                  ? <QesIcon iconName={FontAwesome.CheckCircle} color={"#ffd654"} description="Warning: this provider was attested more than 90 days ago" className="align-text-top" width="12" />
                  // color is "success" green equivalent to $qes-tertiary-green-2
                  : <QesIcon iconName={FontAwesome.CheckCircle} color={"#00cd87"} isDecorative className="align-text-top" width="12" />
                }
                {bottomProviderDaysSinceLastAttested} days prior
              </React.Fragment>);
          } else if (comparison.comparisonProviderType !== ComparisonProviderType.Matched && ((comparison.comparisonProviderType !== ComparisonProviderType.Added))) {
            // If the compared provider is on bottom, AND he was not added, AND we do not have a match
            // -OR- if the compared provider is on top, AND he was not removed, AND we do not have a match
            bottomProviderAttestationInfoElement = null;
          }

          return (
            <>
              <div className="compared-providers-content">{topProviderAttestationInfoElement}</div>
              <div className="compared-providers-content">{bottomProviderAttestationInfoElement}</div>
            </>
          );
        })(),
      });
    }
    /* oxlint-enable sort-keys */
    return topData;
  }

  private handleSort = (data: NetworkProvidersComparisonTableData[], column: keyof NetworkProvidersComparisonTableData, direction: SortDirections) => {
    // We always sort on the selected* key and never comparison*
    // api values taken from FlatComparisonProviderModel.cs
    let apiModelKey: string = column;
    switch (column) {
      case "comparisonProviderType":
        apiModelKey = "ComparisonProviderType";
        break;
      case "displayName":
        apiModelKey = "SelectedProviderName";
        break;
      case "address":
        apiModelKey = "SelectedProviderAddress";
        break;
      case "servesPercent":
        apiModelKey = "SelectedProviderServesPercent";
        break;
      case "medicareProviderOptOut":
        apiModelKey = "SelectedProviderMedicareOptOut";
        break;
      case "providerDaysSinceLastAttested":
        apiModelKey = "SelectedProviderDaysSinceLastAttested";
        break;
    }
    this.buildServicingProviders(false, true, apiModelKey, direction);
    return data;
  }

  private handleLoadMore = (justChecking: boolean) => {
    if (!justChecking) {
      this.buildServicingProviders(false, false, this.state.lastSortedColumn, this.state.lastSortedDirection);
    }
    return this.state.sortedRecords.length < this.state.totalCount;
  }

  /**
   * Get the rendered address for the top row information in the comparison.
   * @param address The first-line address
   * @param address2 The second-line address
   * @param city The city
   * @param state The state abbreviation
   * @param zip The zip code
   * @param phone The unformatted phone number
   */
  private getAddressDisplay(address: string, address2: string, city: string, state: string, zip: string, phone: string, fax: string): React.ReactNode {
    return address ? (
      <React.Fragment>
        {address ? <React.Fragment>{address}<br /></React.Fragment> : null}
        {address2 ? <React.Fragment>{address2}<br /></React.Fragment> : null}
        {city}
        {city && (state || zip) ? ", " : null}
        {state} {zip}
        {phone ? <br /> : null}
        {phone ?
          <React.Fragment>
            <TooltipWrapper text="Phone" position="bottom">
              <QesIcon iconName={FontAwesome.Phone} description="Phone number" width="11" className="align-baseline d-inline-block me-1" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(phone)}
          </React.Fragment> :
          null
        }
        {fax ? <br /> : null}
        {fax ?
          <React.Fragment>
            <TooltipWrapper text="Fax" position="bottom">
              <QesIcon iconName={FontAwesome.Fax} description="Fax number" width="14" className="align-baseline d-inline-block me-1" />
            </TooltipWrapper>
            &nbsp; {formatPhoneNumber(fax)}
          </React.Fragment> :
          null
        }
        {!address2 && address ? <br /> : null}
      </React.Fragment>
    ) : null;
  }

  /**
   * Fetches and populates the collection of ServicingProviderComparisons. This is is primary state maintainer for loading status.
   * @param isInitialLoad Specifies if this call should be considered the initial load of results for the component
   * @param forceLoad OPTIONAL: If true, existing results are wiped out, and replaced with new results. If false or undefined, new results are appended to old results.
   * @param selectedColumn OPTIONAL: Tells the server to sort on a particular key
   * @param sortDirection OPTIONAL: Tells the server which direction to sort
   */
  private buildServicingProviders = async (isInitialLoad: boolean, forceLoad?: boolean, selectedColumn?: string, sortDirection?: SortDirections) => {
    // Guards against a slower, superseded request resolving after a newer one and overwriting its results.
    let requestSequence: number;
    try {
      const nextLoadStatus: LoadStatus = this.state.loadStatus === "shouldLoad" || forceLoad
        ? "loading"
        : (this.state.loadStatus === "loaded" && this.state.offset < this.state.totalCount)
          ? "partialLoading"
          : null;

      if (nextLoadStatus) {
        this.setState({
          isInitialLoad,
          loadStatus: nextLoadStatus,
        });

        requestSequence = ++this.latestRequestSequence;

        const isComparedRunConfigEarlier = shouldSwitchProjectRunConfigurationsInCompareMode(this.props.runConfiguration, this.props.compareToRunConfiguration);

        let offset = this.state.offset;
        if ((selectedColumn && selectedColumn !== this.state.lastSortedColumn) || (sortDirection !== undefined && sortDirection !== this.state.lastSortedDirection)) {
          offset = 0;
        }
        const servicingProviderComparisonResult = await this.projectService.getServicingComparisonProviders(
          this.props.project.projectId,
          isComparedRunConfigEarlier ? this.props.compareToRunConfiguration.projectRunConfigurationId : this.props.runConfiguration.projectRunConfigurationId,
          isComparedRunConfigEarlier ? this.props.runConfiguration.projectRunConfigurationId : this.props.compareToRunConfiguration.projectRunConfigurationId,
          this.props.regionSetId,
          this.props.specialtyGroupCode,
          ProviderNetworkTypeEnum.Network,
          this.isZipProject ? NetworkAssessmentAnalysisType.ServiceArea : this.props.runConfiguration.networkAssessmentAnalysisType,
          this.state.showAddedProviders,
          this.state.showRemovedProviders,
          this.state.showMatchedProviders,
          offset,
          this.batchCount,
          selectedColumn,
          sortDirection,
        );

        if (requestSequence !== this.latestRequestSequence) {
          return;
        }

        for (const servicingProviderComparison of servicingProviderComparisonResult.providers) {
          servicingProviderComparison.key = this.getProviderComparisonKey(servicingProviderComparison);
        }

        let loadStatus: LoadStatus = "error";
        let records: ServicingProviderComparison[] = [];

        if (servicingProviderComparisonResult && servicingProviderComparisonResult.providers) {
          if (servicingProviderComparisonResult.providers.length > 0 && forceLoad) {
            // If this is a forced reload, we need to clear out old results
            loadStatus = "loaded";
            records = servicingProviderComparisonResult.providers;
          } else if (servicingProviderComparisonResult.providers.length > 0) {
            // If this is not a forced reload, we need to append to existing results, hence the spread
            loadStatus = "loaded";
            records = [...this.state.sortedRecords, ...servicingProviderComparisonResult.providers];
          } else {
            loadStatus = "empty";
          }
        }

        this.setState({
          loadStatus,
          offset: offset + servicingProviderComparisonResult.batchCount,
          sortedRecords: records,
          tableData: this.buildTable(records),
          totalCount: servicingProviderComparisonResult.totalCount,
        });
      }
    } catch (ex) {
      if (requestSequence !== undefined && requestSequence !== this.latestRequestSequence) {
        return;
      }
      this.setState({
        isInitialLoad,
        loadStatus: "error",
        sortedRecords: [],
        tableData: [],
      });
    }
  }

  /**
   * Gets a unique key for the provider comparison, using information from both the selected configuration and compared configuration providers.
   * @param providerComparison The comparison to determine the key from.
   */
  private getProviderComparisonKey(providerComparison: ServicingProviderComparison): string {
    return `${providerComparison.selectedProviderNPI}:${providerComparison.selectedProviderProviderId}compared${providerComparison.comparedProviderNPI}:${providerComparison.comparedProviderProviderId}`;
  }

  /**
   * Gets the provider profile content for the Flyout.
   * It converts a ServicingProviderComparison to a ServicingProvider using either the selected configuration or compared configuration
   * provider's information.
   * @param comparison The comparison, which contains the provider information needed to populate the Flyout
   * @param convertFromComparedProvider If true, the compared run configuration provider's information is used to populate the ServicingProvider profile. Else, the selected run configuration provider's information is used.
   */
  private getProviderProfile = (comparison: ServicingProviderComparison, comparedProvider: boolean, runConfiguration: ProjectRunConfigurationResult): React.ReactNode => {
    let provider: ServicingProvider = null;

    if (comparedProvider) {
      provider = {
        acceptingNewPatients: comparison.comparedProviderAcceptingNewPatients,
        acceptsMedicareAssignment: comparison.comparedProviderAcceptsMedicareAssignment,
        address: comparison.comparedProviderAddress,
        address2: comparison.comparedProviderAddress2,
        city: comparison.comparedProviderCity,
        contractType: comparison.comparedProviderContractType,
        countyFIPS: comparison.comparedProviderCountyFIPS,
        countySSA: comparison.comparedProviderCountySSA,
        credentials: comparison.comparedProviderCredentials,
        daysSinceLastAttested: comparison.comparedProviderDaysSinceLastAttested,
        deactivatedNPI: comparison.comparedProviderDeactivatedNPI,
        displayName: comparison.comparedProviderDisplayName,
        email: comparison.comparedProviderEmail,
        employmentStatus: comparison.comparedProviderEmploymentStatus,
        entityType: comparison.comparedProviderEntityType,
        externLocationId: comparison.comparedProviderExternLocationId,
        externProviderId: comparison.comparedProviderExternProviderId,
        externRowId: comparison.comparedProviderExternRowId,
        externSpecialtyCode: comparison.comparedProviderExternSpecialtyCode,
        externZip: comparison.comparedProviderExternZip,
        fax: comparison.comparedProviderFax,
        firstName: comparison.comparedProviderFirstName,
        gainsPercent: comparison.comparedProviderGainsPercent,
        gender: comparison.comparedProviderGender,
        geoInfoDisplay: comparison.comparedProviderGeoInfoDisplay,
        impactsPercent: comparison.comparedProviderImpactsPercent,
        invalidNPI: comparison.comparedProviderInvalidNPI,
        isRecentlyAttested: comparison.comparedProviderIsRecentlyAttested,
        lastAttestedDate: comparison.comparedProviderLastAttestedDate,
        lastName: comparison.comparedProviderLastName,
        latitude: comparison.comparedProviderLatitude,
        longitude: comparison.comparedProviderLongitude,
        medGroupAffiliation: comparison.comparedProviderMedGroupAffiliation,
        medicareCCN: comparison.comparedProviderMedicareCCN,
        medicareCertifiedBeds: comparison.comparedProviderMedicareCertifiedBeds,
        medicareOptOut: comparison.comparedProviderMedicareOptOut,
        middleName: comparison.comparedProviderMiddleName,
        missingNPI: comparison.comparedProviderMissingNPI,
        name: comparison.comparedProviderName,
        npi: comparison.comparedProviderNPI,
        oigExcluded: comparison.comparedProviderOigExcluded,
        oigWaiver: comparison.comparedProviderOigWaiver,
        organizationName: comparison.comparedProviderOrganizationName,
        organizationNPI: comparison.comparedProviderOrganizationNPI,
        phone: comparison.comparedProviderPhone,
        planningRegion: comparison.comparedProviderPlanningRegion,
        precluded: comparison.comparedProviderPrecluded,
        prefix: comparison.comparedProviderPrefix,
        processed: comparison.comparedProviderProcessed,
        providerId: comparison.comparedProviderProviderId,
        servesPercent: comparison.comparedProviderServesPercent,
        specialty: comparison.comparedProviderSpecialty,
        specialtyCode: comparison.comparedProviderSpecialtyCode,
        specialtyName: comparison.comparedProviderSpecialtyName,
        state: comparison.comparedProviderState,
        suffix: comparison.comparedProviderSuffix,
        taxonomyCode: comparison.comparedProviderTaxonomyCode,
        tin: "", //Not populated by the API. If it needs to be displayed, then it has to be role restricted at the API side.
        underContract: comparison.comparedProviderUnderContract,
        zip: comparison.comparedProviderZip,
      };
    } else {
      provider = {
        acceptingNewPatients: comparison.selectedProviderAcceptingNewPatients,
        acceptsMedicareAssignment: comparison.selectedProviderAcceptsMedicareAssignment,
        address: comparison.selectedProviderAddress,
        address2: comparison.selectedProviderAddress2,
        city: comparison.selectedProviderCity,
        contractType: comparison.selectedProviderContractType,
        countyFIPS: comparison.selectedProviderCountyFIPS,
        countySSA: comparison.selectedProviderCountySSA,
        credentials: comparison.selectedProviderCredentials,
        daysSinceLastAttested: comparison.selectedProviderDaysSinceLastAttested,
        deactivatedNPI: comparison.selectedProviderDeactivatedNPI,
        displayName: comparison.selectedProviderDisplayName,
        email: comparison.selectedProviderEmail,
        employmentStatus: comparison.selectedProviderEmploymentStatus,
        entityType: comparison.selectedProviderEntityType,
        externLocationId: comparison.selectedProviderExternLocationId,
        externProviderId: comparison.selectedProviderExternProviderId,
        externRowId: comparison.selectedProviderExternRowId,
        externSpecialtyCode: comparison.selectedProviderExternSpecialtyCode,
        externZip: comparison.selectedProviderExternZip,
        fax: comparison.selectedProviderFax,
        firstName: comparison.selectedProviderFirstName,
        gainsPercent: comparison.selectedProviderGainsPercent,
        gender: comparison.selectedProviderGender,
        geoInfoDisplay: comparison.selectedProviderGeoInfoDisplay,
        impactsPercent: comparison.selectedProviderImpactsPercent,
        invalidNPI: comparison.selectedProviderInvalidNPI,
        isRecentlyAttested: comparison.selectedProviderIsRecentlyAttested,
        lastAttestedDate: comparison.selectedProviderLastAttestedDate,
        lastName: comparison.selectedProviderLastName,
        latitude: comparison.selectedProviderLatitude,
        longitude: comparison.selectedProviderLongitude,
        medGroupAffiliation: comparison.selectedProviderMedGroupAffiliation,
        medicareCCN: comparison.selectedProviderMedicareCCN,
        medicareCertifiedBeds: comparison.selectedProviderMedicareCertifiedBeds,
        medicareOptOut: comparison.selectedProviderMedicareOptOut,
        middleName: comparison.selectedProviderMiddleName,
        missingNPI: comparison.selectedProviderMissingNPI,
        name: comparison.selectedProviderName,
        npi: comparison.selectedProviderNPI,
        oigExcluded: comparison.selectedProviderOigExcluded,
        oigWaiver: comparison.selectedProviderOigWaiver,
        organizationName: comparison.selectedProviderOrganizationName,
        organizationNPI: comparison.selectedProviderOrganizationNPI,
        phone: comparison.selectedProviderPhone,
        planningRegion: comparison.selectedProviderPlanningRegion,
        precluded: comparison.selectedProviderPrecluded,
        prefix: comparison.selectedProviderPrefix,
        processed: comparison.selectedProviderProcessed,
        providerId: comparison.selectedProviderProviderId,
        servesPercent: comparison.selectedProviderServesPercent,
        specialty: comparison.selectedProviderSpecialty,
        specialtyCode: comparison.selectedProviderSpecialtyCode,
        specialtyName: comparison.selectedProviderSpecialtyName,
        state: comparison.selectedProviderState,
        suffix: comparison.selectedProviderSuffix,
        taxonomyCode: comparison.selectedProviderTaxonomyCode,
        tin: "", //Not populated by the API. If it needs to be displayed, then it has to be role restricted at the API side.
        underContract: comparison.selectedProviderUnderContract,
        zip: comparison.selectedProviderZip,
      };
    }

    return (
      <ServicingProviderProfile
        project={this.props.project}
        runConfiguration={runConfiguration}
        regionSetId={this.props.regionSetId}
        specialtyGroupCode={this.props.specialtyGroupCode}
        provider={provider}
        providerNetworkType={comparedProvider ? comparison.comparedProviderNetworkType : comparison.selectedProviderNetworkType}
      />
    );
  }

  private getExportLink = () => {
    const providerIds: number[] = null;
    const selectedMemberships: number[] = [this.props.runConfiguration.memberSetId];
    if (this.props.isDualMembershipMode) {
      selectedMemberships.push(this.props.compareToRunConfiguration.memberSetId);
    }
    const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(this.props.project.projectRunConfigurations, this.props.runConfiguration.projectRunConfigurationId);

    const state: Partial<ExportDataRouteState> & ReportCustomizationRouteState = {
      activeModule: this.props.module,
      activeNetworkType: ProviderNetworkTypeEnum.Network,
      adequacyFilters: this.props.adequacyFilters,
      availableExportTypes: this.isZipProject ? [ExportType.ZipAnalysisAdequacyServicingProvider] : [ExportType.ServicingProviders],
      // the exportComparedRunConfigurationId might be the compareToRunConfigurationId or it might be the runConfigurationId
      // it just depends on which one is older
      comparedRunConfigurationId: this.props.exportComparedRunConfigurationId,
      filters: this.props.filters,
      granularity: RegionGranularity.County,
      initialNetworkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
      networkTypes: this.props.runConfiguration && this.props.runConfiguration.networkTypes as unknown as ProviderNetworkTypeEnum[] || undefined,
      projectResourceId: this.props.project.resource.resourceId,
      projectResourceName: this.props.project.resource.name,
      projectURL: this.props.projectURL,
      providerIds,
      regionSetIds: [this.props.regionSetId],
      showAdvancedComparison: this.props.project.showAdvancedComparison,
      specialtyGroupCodes: [this.props.specialtyGroupCode],

      // new reportcustomization
      /* oxlint-disable sort-keys */
      defaultReportType: this.isZipProject ? ReportType.ZipAnalysisAdequacyServicingProvider : ReportType.AdequacyServicingProvider,
      projectName: this.props.project.resource.name,
      networkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
      showClosestProviderToMemberReport: this.props.project.showClosestProviderReport,
      primaryMembership: { alias: primaryRunConfiguration.memberSetAlias || primaryRunConfiguration.memberSetName, id: primaryRunConfiguration.memberSetId },
      secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
      selectedMemberships,
      selectedTimeslice: this.props.runConfiguration.projectRunConfigurationId, // runConfigurationId
      isCompareMode: !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
      isZipProject: this.isZipProject,
      selectedComparedTimeslice: this.props.compareToRunConfiguration?.projectRunConfigurationId, // compareToRunConfigurationId
      selectedRegionSetIds: !!this.props.adequacyResult?.details[0] ? [this.props.adequacyResult.details[0].regionSetId] : undefined,
      runFrequency: this.props.runFrequency || TimeFrequency.All,
      showAddedProviders: !!this.props.compareToRunConfiguration,
      showMatchedProviders: !!this.props.compareToRunConfiguration,
      showRemovedProviders: !!this.props.compareToRunConfiguration,
      showPatternOfCare: this.props.project.showPatternOfCareProviders,
      hasProspectProviders: !!this.props.runConfiguration.prospectProviderSetId,
      hasMarketProviders: !!this.props.runConfiguration.marketProviderSetId,
      showExpandableSpecialties: !!this.props.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
      zipProjectSearchParameters: this.isZipProject ? getSearchParametersFromFilterSelections(this.props.filterSelections) : undefined,
      showRegionSummaryReport: this.props.project.hasRegions,
      /* oxlint-enable */
    };
    
    return {
      pathname: this.props.exportEndpoint,
      state,
    };
  }
}

const EnhancedNetworkProvidersComparisonPanel = withZipContextState(withFeatureFlagContext(withCountyProjectContext(NetworkProvidersComparisonPanel)));

export { EnhancedNetworkProvidersComparisonPanel as NetworkProvidersComparisonPanel };
export { NetworkProvidersComparisonPanel as NetworkProvidersComparisonPanelClass };
